import React, { useState, useEffect } from 'react';
import { waitlistService } from '../services/waitlist';
import { WaitlistTopNav } from '../components/WaitlistTopNav';
import { Footer } from '../components/Footer';
import { Users, Mail, TrendingUp, Calendar } from 'lucide-react';

export const WaitlistAdminPage: React.FC = () => {
  const [stats, setStats] = useState<{ total: number; recent: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const waitlistStats = await waitlistService.getWaitlistStats();
      setStats(waitlistStats);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStats = () => {
    loadStats();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WaitlistTopNav />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WaitlistTopNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Waitlist Dashboard</h1>
          <p className="text-gray-600">Monitor your waitlist signups and growth</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Waitlist</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.recent || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Launch Date</p>
                <p className="text-2xl font-bold text-gray-900">Coming Soon</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            <button
              onClick={refreshStats}
              className="bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors"
            >
              Refresh Stats
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Export Waitlist</h3>
              <p className="text-sm text-gray-600 mb-3">Download all waitlist emails for your email marketing platform</p>
              <button className="text-brand-500 hover:text-brand-600 text-sm font-medium">
                Export CSV →
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Send Update</h3>
              <p className="text-sm text-gray-600 mb-3">Send a progress update to your waitlist</p>
              <button className="text-brand-500 hover:text-brand-600 text-sm font-medium">
                Compose Email →
              </button>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Pro Tips</h3>
          <ul className="text-blue-800 space-y-2">
            <li>• Send weekly progress updates to keep your waitlist engaged</li>
            <li>• Share behind-the-scenes development progress</li>
            <li>• Offer exclusive early-bird pricing to waitlist members</li>
            <li>• Consider A/B testing different messaging on your landing page</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};
